import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { indexLines, lineFor, lockOf, lockReasonKey, type AuthLine, type AuthorisationSummary, type SourceLock } from '../insurance/authorisation';

export interface AuthorisationScope {
  encounterId?: string;
  invoiceId?: string;
}

export interface AuthorisationValue {
  summary: AuthorisationSummary | null;
  lineFor: (keys: readonly (string | null | undefined)[]) => AuthLine | null;
  lockOf: (keys: readonly (string | null | undefined)[]) => SourceLock;
  lockReasonKey: (keys: readonly (string | null | undefined)[]) => ReturnType<typeof lockReasonKey>;
  invalidate: () => void;
}

const NONE: AuthorisationValue = {
  summary: null,
  lineFor: () => null,
  lockOf: () => 'open',
  lockReasonKey: () => null,
  invalidate: () => undefined,
};

const AuthorisationContext = createContext<AuthorisationValue | null>(null);

export function authorisationQueryKey(scope: AuthorisationScope): [string, AuthorisationScope] {
  return ['authorisation', scope];
}

export function useAuthorisationSummary(scope: AuthorisationScope, enabled: boolean) {
  return useQuery({
    queryKey: authorisationQueryKey(scope),
    queryFn: () => get<AuthorisationSummary>(`/insurance/authorisation${qs({ ...scope })}`),
    enabled: enabled && Boolean(scope.encounterId || scope.invoiceId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useAuthorisationValue(scope: AuthorisationScope, enabled: boolean): AuthorisationValue {
  const queryClient = useQueryClient();
  const query = useAuthorisationSummary(scope, enabled);
  const summary = query.data ?? null;
  return useMemo(() => {
    const index = indexLines(summary?.lines);
    const find = (keys: readonly (string | null | undefined)[]) => lineFor(index, keys);
    return {
      summary,
      lineFor: find,
      lockOf: (keys) => lockOf(find(keys)),
      lockReasonKey: (keys) => lockReasonKey(lockOf(find(keys))),
      invalidate: () => void queryClient.invalidateQueries({ queryKey: ['authorisation'] }),
    };
  }, [summary, queryClient]);
}

export function AuthorisationProvider({ encounterId, enabled = true, children }: { encounterId: string; enabled?: boolean; children: ReactNode }) {
  const value = useAuthorisationValue({ encounterId }, enabled);
  return <AuthorisationContext.Provider value={value}>{children}</AuthorisationContext.Provider>;
}

export function useAuthorisation(): AuthorisationValue {
  return useContext(AuthorisationContext) ?? NONE;
}
